import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import ProgressiveRegistrationForm from './pages/progressive-registration-form';
import AdminDashboard from './pages/admin-dashboard';
import EmployeeProfileHub from './pages/employee-profile-hub';
import CompanySelectionHub from './pages/company-selection-hub';
import AnalyticsOverview from './pages/analytics-overview';
import CompanySpecificLandingPages from './pages/company-specific-landing-pages';
import SignIn from './pages/auth/SignIn';
import SignUp from './pages/auth/SignUp';
import EmailConfirm from "pages/auth/EmailConfirm";
import { useAuth } from './contexts/AuthContext';
import EmailTest from "pages/EmailTest";
import AdminUserManagementSystem from './pages/admin-user-management-system';
import AdminUserCreationForm from './pages/admin-user-creation-form';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  return children;
};

// Admin Protected Route Component - CRITICAL FIX
const AdminRoute = ({ children }) => {
  const { user, profile, loading } = useAuth();
  const [timeoutReached, setTimeoutReached] = React.useState(false);

  // Set a timeout to prevent infinite loading
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setTimeoutReached(true);
    }, 3000); // 3 second timeout

    return () => clearTimeout(timer);
  }, []);

  // If no user after initial load, redirect to sign in
  if (!loading && !user) {
    return <Navigate to="/signin" replace />;
  }

  // If user exists, allow access even if profile is still loading
  // The dashboard will handle its own loading states
  if (user) {
    // If profile is loaded and user is not admin, redirect
    if (profile && profile?.role !== 'admin') {
      console.log('Non-admin user attempted to access admin route. Redirecting to company selection.');
      return <Navigate to="/company-selection-hub" replace />;
    }

    // User is authenticated - allow access
    // Dashboard will handle its own loading/error states
    return children;
  }

  // Still loading and no user yet - show loading only if timeout not reached
  if (loading && !timeoutReached) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-lg text-gray-600 mb-2">Loading...</div>
          <div className="text-sm text-gray-500">Checking authentication</div>
        </div>
      </div>
    );
  }

  // Timeout reached or other state - redirect to sign in
  return <Navigate to="/signin" replace />;
};

export default function ProjectRoutes() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Public Auth Routes */}
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/auth/confirm" element={<EmailConfirm />} />
          
          {/* Public Homepage - No authentication required */}
          <Route path="/" element={<CompanySelectionHub />} />
          <Route path="/company-selection-hub" element={<CompanySelectionHub />} />
          
          {/* Public Registration Form - No authentication required for employee registration */}
          <Route path="/progressive-registration-form" element={<ProgressiveRegistrationForm />} />
          
          {/* Protected Routes - Authentication required */}
          <Route 
            path="/employee-profile-hub" 
            element={
              <ProtectedRoute>
                <EmployeeProfileHub />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/company-specific-landing-pages" 
            element={
              <ProtectedRoute>
                <CompanySpecificLandingPages />
              </ProtectedRoute>
            } 
          />
          
          {/* Admin Only Routes */}
          <Route 
            path="/admin-dashboard" 
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } 
          />
          <Route 
            path="/analytics-overview" 
            element={
              <AdminRoute>
                <AnalyticsOverview />
              </AdminRoute>
            } 
          />
          
          <Route path="/email-test" element={<EmailTest />} />
          
          {/* Admin Management Routes */}
          <Route
            path="/admin-user-management-system"
            element={
              <AdminRoute>
                <AdminUserManagementSystem />
              </AdminRoute>
            }
          />
          <Route
            path="/admin-user-creation-form"
            element={
              <AdminRoute>
                <AdminUserCreationForm />
              </AdminRoute>
            }
          />
          
          {/* 404 Not Found */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}